/**
 * Which look My Library uses: today's table ("classic"), picture cards ("gallery"), or each video
 * with its best clips beside it ("strips").
 *
 * Everyone starts on the site's default, one row on the backend (`/api/v1/site-settings/`, the same
 * row as the home-page switch) that only an admin can change. A person who picks a look keeps it
 * in their own browser (localStorage). The page's frame and the Uploads tab both read the look, so
 * a change is broadcast with a window event and every reader re-renders.
 */
import { useCallback, useEffect, useState } from "react";
import { hostApi } from "../../../constants";
import apiRequest from "../../../../utils/apiClient";

export type LibraryLook = "classic" | "gallery" | "strips";
export const LIBRARY_LOOKS: LibraryLook[] = ["gallery", "strips", "classic"];

const ENDPOINT = `${hostApi}site-settings/`;
const MINE_KEY = "cc_library_look";
const SITE_KEY = "cc_library_look_site";
const EVENT = "cc-library-look";

const isLook = (v: unknown): v is LibraryLook => LIBRARY_LOOKS.includes(v as LibraryLook);

const read = (key: string): LibraryLook | null => {
  try {
    const v = localStorage.getItem(key);
    return isLook(v) ? v : null;
  } catch (e) {
    return null;
  }
};

const write = (key: string, v: LibraryLook | null) => {
  try {
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT));
};

interface LookState {
  /** What this person sees. */
  look: LibraryLook;
  /** What everyone sees until they pick one themselves. */
  siteLook: LibraryLook;
  /** An admin account: may change siteLook. */
  canEdit: boolean;
  setLook: (look: LibraryLook) => void;
  makeDefault: (look: LibraryLook) => Promise<void>;
}

/** `?v=<id>` (a quick look, e.g. from an email) needs the new looks; Classic falls to Strips for it. */
export const lookForPage = (look: LibraryLook, search: string): LibraryLook =>
  look === "classic" && new URLSearchParams(search).has("v") ? "strips" : look;

export const useLibraryLook = (): LookState => {
  const [, bump] = useState(0);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const onChange = () => bump((n) => n + 1);
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequest(ENDPOINT, { method: "GET" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body) return;
        setCanEdit(body.can_edit === true);
        if (isLook(body.library_look) && body.library_look !== read(SITE_KEY)) write(SITE_KEY, body.library_look);
      })
      .catch(() => { /* keep the cached default */ });
    return () => { cancelled = true; };
  }, []);

  const siteLook = read(SITE_KEY) || "classic";
  const look = read(MINE_KEY) || siteLook;

  const setLook = useCallback((next: LibraryLook) => write(MINE_KEY, next), []);

  const makeDefault = useCallback(async (next: LibraryLook) => {
    const res = await apiRequest(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ library_look: next }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error || `Couldn't save (${res.status})`);
    write(SITE_KEY, isLook(body.library_look) ? body.library_look : next);
  }, []);

  return { look, siteLook, canEdit, setLook, makeDefault };
};
